import { Theme } from '../../services/ui/utils';
import { WidgetAuthMode } from '../../services/widget';
import { HTMLMediaEventType } from '../../components/playerAdapter/htmlMediaSchema';

export type PlayerWidgetIFrameParams = {
  jobId: string,
  teamId?: string | null,
  time?: number | 'none',
  momentId?: string,
  auth?: WidgetAuthMode | null,
  showIndex?: boolean | null,
  showComments?: boolean | null,
  showTranscript?: boolean | null,
  allowFullscreen?: boolean | null,
  showProfile?: boolean | null,
  showTopicTags?: boolean | null,
  showEmbedIcon?: boolean | null,
  showHelp?: boolean | null,
  disableReactions?: boolean | null,
  disableComments?: boolean | null,
  disableIndex?: boolean | null,
  disableTranscript?: boolean | null,
  autoplay?: boolean | null,
  allowShare?: boolean | null,
  allowDownload?: boolean | null,
  theme?: Theme | null,
  customRedirect?: string | null,
}
const allowAttributes = ['autoplay', 'encrypted-media', 'picture-in-picture', 'clipboard-write', 'fullscreen'];

export function getPlayerWidgetIFrameCode(params: PlayerWidgetIFrameParams) {

  /** Example from youtube
   * <iframe 
   *  width="1280" 
   *  height="555" 
   *  src="https://www.youtube.com/embed/4Y6EVouZm-I" 
   *  title="YouTube video player" 
   *  frameborder="0" 
   *  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
   *  allowfullscreen>
   * </iframe>
   */

  const iframe = document.createElement('iframe');
  iframe.src = getPlayerWidgetIFrameSrc(params).toString();
  iframe.width = '800';
  iframe.height = '450';
  iframe.title = 'CLIPr Widget';

  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allow', allowAttributes.join('; '));
  iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-downloads allow-orientation-lock');

  let code = iframe.outerHTML;


  // Reverse browser URL-encoding of ampersands in embed code
  code = code.replaceAll(/&amp;/gi, '&');
  return code;
}

export function getPlayerWidgetIFrameSrc(params: PlayerWidgetIFrameParams) {
  const url = buildUrlSearchParams(params, new URL(`${window.location.origin}/widget/${params.jobId}`));
  return url;
}

export function buildUrlSearchParams(params: PlayerWidgetIFrameParams, url: URL, jobId?: string) {
  const time = params.time === 'none' ? '' : (params.time ?? 0).toString();
  const theme = params.theme && params.theme !== Theme.Default ? params.theme.toLocaleLowerCase() : 'dark';

  if (jobId) {
    url.searchParams.append('jobId', jobId);
  }

  if (params.momentId) {
    url.searchParams.append('time', time);
    url.searchParams.append('momentId', params.momentId.toString());
  }

  if (params.teamId) {
    url.searchParams.append('teamId', params.teamId.toString());
  }

  if (params.customRedirect) {
    const redirectUrl = encodeURIComponent(decodeURIComponent(params.customRedirect));
    url.searchParams.append('customRedirect', redirectUrl);
  }

  // Only emit a param when it deviates from the widget reader's default, so share
  // URLs stay short. theme/autoplay are kept unconditional: their reader has no
  // default, so omitting them would flip the widget's behavior.
  const appendIfNotDefault = (key: string, value: boolean | null | undefined, defaultValue: boolean) => {
    const effective = value ?? defaultValue;
    if (effective !== defaultValue) {
      url.searchParams.append(key, effective.toString());
    }
  };

  url.searchParams.append('theme', theme);
  url.searchParams.append('autoplay', params.autoplay?.toString() ?? 'true');
  appendIfNotDefault('allowShare', params.allowShare, true);
  appendIfNotDefault('allowDownload', params.allowDownload, true);
  appendIfNotDefault('allowFullscreen', params.allowFullscreen, true);
  appendIfNotDefault('showHelp', params.showHelp, true);
  appendIfNotDefault('disableReactions', params.disableReactions, false);
  appendIfNotDefault('disableComments', params.disableComments, false);
  appendIfNotDefault('disableTranscript', params.disableTranscript, false);
  appendIfNotDefault('disableIndex', params.disableIndex, false);
  appendIfNotDefault('showIndex', params.showIndex, true);
  appendIfNotDefault('showComments', params.showComments, false);
  appendIfNotDefault('showTranscript', params.showTranscript, false);
  appendIfNotDefault('showProfile', params.showProfile, true);
  appendIfNotDefault('showTopicTags', params.showTopicTags, true);
  appendIfNotDefault('showEmbedIcon', params.showEmbedIcon, true);

  return url;
}

/**
 * Types and constants to be used by the PlayerWidgetAdapter to comply with the Player.js approach.
 */
export type MediaEventType = HTMLMediaEventType | 'ready';
export const Context = 'player.js';
export const Version = '1.0.0';
export const Events: MediaEventType[] = [
  'ready',
  'play',
  'pause',
  'timeupdate',
  'ended'
];
export type MethodType = 'play' |
  'pause' |
  'getPaused' |
  'mute' |
  'unmute' |
  'getMuted' |
  'setVolume' |
  'getVolume' |
  'getDuration' |
  'setCurrentTime' |
  'getCurrentTime' |
  'setLoop' |
  'getLoop' |
  'addEventListener' |
  'removeEventListener';
export const Methods: MethodType[] = [
  'play',
  'pause',
  'getPaused',
  'mute',
  'unmute',
  'getMuted',
  'setVolume',
  'getVolume',
  'getDuration',
  'setCurrentTime',
  'getCurrentTime',
  // 'setLoop',
  // 'getLoop',
  'addEventListener',
  'removeEventListener'
]